import React, { ReactNode, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { CloseOutlined } from '@ant-design/icons';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import { translate } from '@portal/services/i18n';

type ButtonActions = {
  label?: string;
  onClick?: () => void;
  type?: 'cancel' | 'ok' | 'danger';
  disabled?: boolean;
  loading?: boolean;
};

type ModalProps = {
  size?: 'small' | 'medium' | 'large';
  title: string;
  description?: string;
  visible: boolean;
  children: ReactNode;
  onClose?: () => void;
  actions?: ButtonActions[];
  autoHeight?: boolean;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  closable?: boolean;
};

const getStableId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2)}`;

const AdvancedModal = (props: ModalProps) => {
  const {
    size = 'medium',
    visible,
    title,
    description,
    children,
    actions,
    onClose,
    autoHeight = false,
    closeOnEscape = true,
    closeOnOverlayClick = true,
    closable,
  } = props;

  const titleIdRef = useRef<string | null>(null);
  const descriptionIdRef = useRef<string | null>(null);
  if (titleIdRef.current === null)
    titleIdRef.current = getStableId('advanced-modal-title');
  if (descriptionIdRef.current === null)
    descriptionIdRef.current = getStableId('advanced-modal-desc');

  const titleId = titleIdRef.current;
  const descriptionId = descriptionIdRef.current;
  const previousOverflowRef = useRef<string | null>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const isClosable = closable ?? !!onClose;

  useEffect(() => {
    if (!visible) return;
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflowRef.current ?? '';
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    contentRef.current?.focus({ preventScroll: true });
    return () => {
      previousActiveElementRef.current?.focus({ preventScroll: true });
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !closeOnEscape || !onClose) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, closeOnEscape, onClose]);

  if (!visible) return null;

  const modalSizeClass = size ? `advanced-modal__content--${size}` : '';

  const getMaxWidth = () => {
    switch (size) {
      case 'small':
        return '520px';
      case 'medium':
        return '700px';
      case 'large':
        return '800px';
      default:
        return '600px';
    }
  };

  const getHeight = () => {
    switch (size) {
      case 'small':
        return '400px';
      case 'large':
        return '80%';
      default:
        return '95%';
    }
  };

  const handleOverlayClick = () => {
    if (closeOnOverlayClick && onClose) onClose();
  };

  const showHeader = !!(title || description || (isClosable && onClose));

  const modalContent = (
    <div
      className="advanced-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId ?? undefined : undefined}
      aria-describedby={description ? descriptionId ?? undefined : undefined}
    >
      <div
        className="advanced-modal__overlay"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />
      <div
        ref={contentRef}
        tabIndex={-1}
        className={`advanced-modal__content ${modalSizeClass}`}
        style={{
          maxWidth: getMaxWidth(),
          height: autoHeight ? 'auto' : getHeight(),
        }}
      >
        {showHeader ? (
          <header className="advanced-modal__header">
            <div className="advanced-modal__header-text">
              {title ? (
                <h2 id={titleId ?? undefined} className="advanced-modal__title">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p
                  id={descriptionId ?? undefined}
                  className="advanced-modal__description"
                >
                  {description}
                </p>
              ) : null}
            </div>
            {isClosable && onClose ? (
              <button
                type="button"
                className="advanced-modal__close"
                onClick={onClose}
                aria-label={translate('SHARED.CLOSE')}
              >
                <CloseOutlined aria-hidden />
              </button>
            ) : null}
          </header>
        ) : null}
        <div className="advanced-modal__body">{children}</div>
        {actions && actions.length > 0 ? (
          <footer className="advanced-modal__footer">
            {actions.map((button: ButtonActions, index: number) => (
              <AdvancedButton
                key={button.label ? `${button.label}-${index}` : index}
                text={button.label ?? ''}
                onClick={button.onClick}
                variant={button.type === 'cancel' ? 'text' : 'contained'}
                color={
                  button.type === 'danger'
                    ? 'error'
                    : button.type === 'cancel'
                    ? 'secondary'
                    : 'primary'
                }
                disabled={button?.disabled}
                loading={button?.loading}
              />
            ))}
          </footer>
        ) : null}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default AdvancedModal;
